/**
 * Subscription Widget - SealSubscriptions Integration
 * Handles subscription toggle, price updates, and selling plan selection
 * Updates both ATC button AND bundle option prices/savings
 * 
 * Technical Solution:
 * - Fetches actual selling plan IDs from product data
 * - Integrates with SealSubscriptions app selling plans
 * - Handles bundle quantities with subscription plans
 * - Passes selling_plan ID and line item properties to cart
 */

class SubscriptionWidget {
  constructor() {
    this.checkbox = document.querySelector('.subscription-widget__checkbox');
    this.widget = document.querySelector('.subscription-widget');
    
    if (!this.checkbox) {
      console.warn('⚠️ Subscription checkbox not found');
      return;
    }

    // Get discount from checkbox data attribute
    this.discount = parseInt(this.checkbox.dataset.discount || '25') / 100;
    
    // Track state
    this.basePrice = 0;
    this.currentQuantity = 1;
    this.currentPaidQuantity = 1;
    this.currentVariantId = null;
    this.isActive = false;
    
    // Store original bundle data
    this.originalBundleData = [];
    
    // Selling plan configuration
    this.sellingPlanId = null;
    this.sellingPlanAllocation = null;
    this.productData = null;

    console.log('✓ Subscription widget initializing...', {
      discount: this.discount * 100 + '%'
    });

    this.init();
  }

  init() {
    // Get initial price info from bundle selector
    this.updatePriceInfo();
    
    // Store original bundle prices and savings
    this.storeOriginalBundleData();
    
    // Fetch selling plan data from product
    this.fetchSellingPlanData();
    
    // Bind checkbox toggle
    this.checkbox.addEventListener('change', (e) => {
      this.isActive = e.target.checked;
      console.log('Subscription toggled:', this.isActive ? 'ON' : 'OFF');
      
      if (this.isActive) {
        this.applyDiscount();
        this.widget?.classList.add('subscription-widget--active');
        this.setSellingPlan();
      } else {
        this.removeDiscount();
        this.widget?.classList.remove('subscription-widget--active');
        this.clearSellingPlan();
      }
    });

    // Listen for variant changes from bundle selector
    document.addEventListener('variant:changed', (e) => {
      console.log('Variant changed:', e.detail);
      
      // Update current variant tracking
      this.currentVariantId = e.detail.variantId;
      this.currentQuantity = e.detail.quantity;
      this.currentPaidQuantity = e.detail.paidQuantity || e.detail.quantity;
      
      // Fetch new selling plan for this variant
      this.fetchSellingPlanForVariant(e.detail.variantId);
      
      // If subscription is active, update the selling plan
      if (this.isActive) {
        setTimeout(() => {
          this.setSellingPlan();
        }, 300); // Wait for selling plan data to load
      }
    });

    // Listen for bundle price updates (for discount calculations)
    document.addEventListener('bundle:price-updated', (e) => {
      console.log('Bundle price updated:', e.detail);
      this.basePrice = e.detail.basePrice;
      this.currentQuantity = e.detail.quantity;
      this.currentPaidQuantity = e.detail.paidQuantity || e.detail.quantity;
      this.currentVariantId = e.detail.variantId;
      
      // Refresh original bundle data to capture new variant prices
      this.storeOriginalBundleData();
      
      // Bundle selector now handles subscription discount automatically
      // Only update bundle options if subscription is active
      if (this.isActive && !e.detail.isSubscriptionActive) {
        // Fallback: only if bundle selector didn't apply discount
        this.applyDiscount();
      } else if (this.isActive) {
        // Just update bundle options (button price already correct)
        this.updateBundleOptions(true);
      }
    });

    // Client pricing model: single unit is $34.99 with NO discount; the 25%
    // Automatic Refills is opt-in only. Do NOT pre-check the subscription on load
    // (auto-select intentionally disabled — was discounting the default price 25%).
    // this.autoSelectSubscription();

    console.log('✓ Subscription widget ready');
  }

  /**
   * Auto-select subscription on page load
   * Waits for initial data to be loaded before activating
   */
  autoSelectSubscription() {
    let retries = 0;
    const maxRetries = 30; // Max 3 seconds of retries (30 * 100ms)
    
    // Wait for bundle selector to be ready and bundle options to have prices
    const checkReady = () => {
      const checkedBundle = document.querySelector('.bundle-option__radio:checked');
      const bundlePrice = checkedBundle ? checkedBundle.dataset.variantId : null;
      const hasPriceData = this.originalBundleData && this.originalBundleData.length > 0;
      
      if (window.bundleSelectorInstance && checkedBundle && bundlePrice && hasPriceData) {
        console.log('Auto-selecting subscription on page load...');
        
        // Programmatically check the checkbox
        this.checkbox.checked = true;
        
        // Trigger the change event to run the exact same code path as a manual click
        // This ensures all calculations, event listeners, and state updates run properly
        const changeEvent = new Event('change', { bubbles: true });
        this.checkbox.dispatchEvent(changeEvent);
        
        console.log('✓ Subscription auto-selected and calculations applied');
      } else if (retries < maxRetries) {
        retries++;
        console.log(`⏳ Waiting for bundle selector (${retries}/${maxRetries})... bundleSelectorInstance:`, !!window.bundleSelectorInstance, 'checkedBundle:', !!checkedBundle, 'bundlePrice:', bundlePrice, 'hasPriceData:', hasPriceData);
        // Retry if not ready yet
        setTimeout(checkReady, 100);
      } else {
        console.warn('⚠️ Failed to auto-select subscription after max retries. Bundle selector may not be available.');
      }
    };
    
    // Start checking after a delay to allow bundle selector to initialize and fire initial events
    setTimeout(checkReady, 400);
  }

  updatePriceInfo() {
    // Get base price from bundle selector
    const bundleSelector = document.querySelector('.bundle-selector');
    if (bundleSelector?.dataset.basePrice) {
      this.basePrice = parseInt(bundleSelector.dataset.basePrice, 10);
    }

    // Get current quantity from checked bundle
    const checkedBundle = document.querySelector('.bundle-option__radio:checked');
    if (checkedBundle) {
      this.currentQuantity = parseInt(checkedBundle.value, 10);
      this.currentPaidQuantity = parseInt(checkedBundle.dataset.paidQuantity || checkedBundle.value, 10);
    }

    console.log('Price info:', {
      basePrice: this.formatMoney(this.basePrice),
      totalQuantity: this.currentQuantity,
      paidQuantity: this.currentPaidQuantity
    });
  }

  storeOriginalBundleData() {
    const bundleOptions = document.querySelectorAll('.bundle-option');
    
    bundleOptions.forEach((option, index) => {
      const priceEl = option.querySelector('.bundle-option__price');
      const savingsEl = option.querySelector('.bundle-option__savings');
      const comparePriceEl = option.querySelector('.bundle-option__compare-price');
      
      if (priceEl) {
        const comparePriceValue = comparePriceEl ? 
          this.extractPriceValue(comparePriceEl.textContent.trim()) : 0;
        
        this.originalBundleData[index] = {
          price: priceEl.textContent.trim(),
          priceValue: parseInt(priceEl.dataset.price || '0', 10),
          quantity: parseInt(priceEl.dataset.quantity || '1', 10),
          paidQuantity: parseInt(priceEl.dataset.paidQuantity || priceEl.dataset.quantity || '1', 10),
          savings: savingsEl ? savingsEl.textContent.trim() : '',
          comparePrice: comparePriceEl ? comparePriceEl.textContent.trim() : '',
          comparePriceValue: comparePriceValue
        };
      }
    });

    console.log('Stored original bundle data:', this.originalBundleData);
  }

  applyDiscount() {
    // Get the current variant price (already in the button from variant selection)
    const currentVariantPrice = this.basePrice || this.getCurrentVariantPrice();
    
    if (!currentVariantPrice) {
      console.warn('⚠️ Variant price not available');
      return;
    }

    // Apply subscription discount to the variant price
    const discountedPrice = Math.round(currentVariantPrice * (1 - this.discount));

    console.log('Applying subscription discount:', {
      variantId: this.currentVariantId,
      quantity: this.currentQuantity,
      originalPrice: this.formatMoney(currentVariantPrice),
      discountedPrice: this.formatMoney(discountedPrice),
      discount: this.discount * 100 + '%'
    });

    this.updateButtonPrice(discountedPrice);

    // Update all bundle option prices and savings
    this.updateBundleOptions(true);
  }

  removeDiscount() {
    // Get the current variant price to restore
    const currentVariantPrice = this.basePrice || this.getCurrentVariantPrice();
    
    if (!currentVariantPrice) return;

    console.log('Removing subscription discount, restoring to:', this.formatMoney(currentVariantPrice));
    this.updateButtonPrice(currentVariantPrice);

    // Restore original bundle option prices and savings
    this.updateBundleOptions(false);
  }

  /**
   * Get current variant price from button or variant data
   */
  getCurrentVariantPrice() {
    // Try to get from button first
    const priceElement = document.querySelector('.product-form__submit .price-item--regular, .product-form__submit .price-item--sale');
    if (priceElement && priceElement.dataset.variantId) {
      // Extract price from data attribute if available
      const dataPrice = parseInt(priceElement.dataset.originalPrice || '0', 10);
      if (dataPrice > 0) return dataPrice;
    }

    // Fallback to basePrice
    return this.basePrice;
  }

  updateButtonPrice(priceInCents) {
    const formattedPrice = this.formatMoney(priceInCents);

    // Find all price elements within the ATC button
    const priceSelectors = [
      '.product-form__submit .price-item--regular',
      '.product-form__submit .price-item--sale',
      '.product-form__submit .price__regular .price-item',
      '.product-form__submit .price__sale .price-item'
    ];

    let updated = 0;
    priceSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        // Skip if this is a compare-at price (strikethrough)
        if (el.tagName === 'S' || el.closest('s')) return;
        
        el.textContent = formattedPrice;
        updated++;
      });
    });

    console.log(`✓ Updated ${updated} button price elements to ${formattedPrice}`);
  }

  updateBundleOptions(applyDiscount) {
    const bundleOptions = document.querySelectorAll('.bundle-option');
    
    bundleOptions.forEach((option, index) => {
      const priceEl = option.querySelector('.bundle-option__price');
      const savingsEl = option.querySelector('.bundle-option__savings');
      const comparePriceEl = option.querySelector('.bundle-option__compare-price');
      
      if (!priceEl || !this.originalBundleData[index]) return;

      const originalData = this.originalBundleData[index];
      // Variant price is already the total for that variant (bundle)
      const variantPrice = originalData.priceValue;

      if (applyDiscount) {
        // Apply subscription discount to the variant price
        const discountedPrice = Math.round(variantPrice * (1 - this.discount));
        priceEl.textContent = this.formatMoney(discountedPrice);

        // Calculate new savings percentage with subscription
        // Savings = (Compare Price - Discounted Subscription Price) / Compare Price
        if (comparePriceEl && savingsEl && originalData.comparePriceValue > 0) {
          const comparePrice = originalData.comparePriceValue;
          const newSavings = Math.round(((comparePrice - discountedPrice) / comparePrice) * 100);
          savingsEl.textContent = `You save ${newSavings}%`;
          
          console.log(`Bundle ${index + 1}: Subscription savings ${newSavings}% (Compare: ${this.formatMoney(comparePrice)}, With Subscription: ${this.formatMoney(discountedPrice)})`);
        }

        console.log(`Bundle ${index + 1}: Subscription price (${this.formatMoney(discountedPrice)})`);
      } else {
        // Restore original values (one-time purchase)
        priceEl.textContent = originalData.price;
        if (savingsEl) {
          savingsEl.textContent = originalData.savings;
        }

        console.log(`Bundle ${index + 1}: Restored one-time price`);
      }
    });

    console.log('✓ Updated all bundle option prices and savings');
  }

  extractPriceValue(priceText) {
    // Extract numeric value from price text like "$99.00"
    const cleaned = priceText.replace(/[^0-9.]/g, '');
    const value = parseFloat(cleaned);
    return value > 0 ? Math.round(value * 100) : 0; // Convert to cents
  }

  /**
   * Fetch selling plan data from product JSON
   * SealSubscriptions creates selling_plan_groups on products
   * Now variant-aware: each variant has its own selling plan
   */
  fetchSellingPlanData() {
    try {
      // Get product data from the product-info element
      const productInfo = document.querySelector('product-info');
      if (!productInfo) {
        console.warn('⚠️ Product info element not found');
        return;
      }

      const productId = productInfo.dataset.productId;
      if (!productId) {
        console.warn('⚠️ Product ID not found');
        return;
      }

      // Fetch product JSON to get selling_plan_groups
      fetch(`${window.location.origin}/products/${window.location.pathname.split('/products/')[1].split('?')[0]}.js`)
        .then(response => response.json())
        .then(product => {
          console.log('📦 Product data loaded:', product);
          
          // Store product data for variant lookups
          this.productData = product;

          // Check if product has selling plans (from SealSubscriptions)
          if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
            console.log('✓ Selling plan groups found:', product.selling_plan_groups.length);
            
            // Get selling plan for current variant
            const currentVariantId = this.getCurrentVariantId();
            if (currentVariantId) {
              this.fetchSellingPlanForVariant(currentVariantId);
            }
          } else {
            console.warn('⚠️ No selling plan groups found on product. Make sure SealSubscriptions app is configured for this product.');
          }
        })
        .catch(error => {
          console.error('❌ Error fetching product data:', error);
        });
    } catch (error) {
      console.error('❌ Error in fetchSellingPlanData:', error);
    }
  }

  /**
   * Fetch selling plan for a specific variant
   * Each variant (1-pack, 3-pack, 5-pack) has its own selling plan
   */
  fetchSellingPlanForVariant(variantId) {
    if (!this.productData) {
      console.warn('⚠️ Product data not loaded yet');
      return;
    }

    const variant = this.productData.variants.find(v => v.id === variantId);
    if (!variant) {
      console.warn('⚠️ Variant not found:', variantId);
      return;
    }

    console.log('🔍 Looking for selling plan for variant:', {
      variantId: variantId,
      variantTitle: variant.title,
      hasAllocations: !!variant.selling_plan_allocations
    });

    // Get selling plan allocations for this variant
    if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
      // Use the first selling plan allocation for this variant
      this.sellingPlanAllocation = variant.selling_plan_allocations[0];
      this.sellingPlanId = this.sellingPlanAllocation.selling_plan_id;

      // Find the full selling plan details
      let sellingPlanDetails = null;
      for (const group of this.productData.selling_plan_groups) {
        const plan = group.selling_plans.find(p => p.id === this.sellingPlanId);
        if (plan) {
          sellingPlanDetails = plan;
          break;
        }
      }

      console.log('✓ Selling plan loaded for variant:', {
        variantId: variantId,
        variantTitle: variant.title,
        sellingPlanId: this.sellingPlanId,
        planName: sellingPlanDetails?.name,
        planDescription: sellingPlanDetails?.description
      });

      // Store in widget for reference
      if (this.widget) {
        this.widget.dataset.sellingPlanId = this.sellingPlanId;
        this.widget.dataset.sellingPlanName = sellingPlanDetails?.name || '';
      }
    } else {
      console.warn('⚠️ No selling plan allocations found for variant:', variantId);
      this.sellingPlanId = null;
      this.sellingPlanAllocation = null;
    }
  }

  /**
   * Get the current variant ID from the form
   */
  getCurrentVariantId() {
    const productForm = document.querySelector('product-form form');
    if (!productForm) return null;
    
    const variantInput = productForm.querySelector('[name="id"]');
    return variantInput ? parseInt(variantInput.value, 10) : null;
  }

  /**
   * Set the selling plan in the product form
   * Uses the actual selling plan ID from SealSubscriptions
   */
  setSellingPlan() {
    const productForm = document.querySelector('product-form form');
    if (!productForm) {
      console.warn('⚠️ Product form not found');
      return;
    }

    // Check if we have a selling plan ID
    if (!this.sellingPlanId) {
      console.warn('⚠️ No selling plan ID available. Waiting for product data to load...');
      // Retry after a short delay
      setTimeout(() => {
        if (this.sellingPlanId) {
          this.setSellingPlan();
        } else {
          console.error('❌ Still no selling plan ID. Check SealSubscriptions configuration.');
        }
      }, 500);
      return;
    }

    // Set the selling_plan input
    let input = productForm.querySelector('[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      productForm.appendChild(input);
    }
    
    // Use the actual selling plan ID from SealSubscriptions
    input.value = this.sellingPlanId;

    // Add line item property to track bundle quantity with subscription
    // This helps in order management and fulfillment
    this.setSubscriptionProperties();

    console.log('✓ Selling plan set:', {
      sellingPlanId: this.sellingPlanId,
      quantity: this.currentQuantity,
      recurringDelivery: `${this.currentQuantity} unit(s) per delivery`
    });
  }

  /**
   * Set line item properties for subscription
   * Tracks bundle quantity and subscription metadata
   */
  setSubscriptionProperties() {
    const productForm = document.querySelector('product-form form');
    if (!productForm) return;

    // Add bundle quantity as a line item property
    // This helps track what quantity was selected with the subscription
    let bundleQtyProperty = productForm.querySelector('[name="properties[_subscription_bundle_qty]"]');
    if (!bundleQtyProperty) {
      bundleQtyProperty = document.createElement('input');
      bundleQtyProperty.type = 'hidden';
      bundleQtyProperty.name = 'properties[_subscription_bundle_qty]';
      productForm.appendChild(bundleQtyProperty);
    }
    bundleQtyProperty.value = this.currentQuantity;

    // Add subscription indicator property
    let subscriptionProperty = productForm.querySelector('[name="properties[_subscription]"]');
    if (!subscriptionProperty) {
      subscriptionProperty = document.createElement('input');
      subscriptionProperty.type = 'hidden';
      subscriptionProperty.name = 'properties[_subscription]';
      productForm.appendChild(subscriptionProperty);
    }
    subscriptionProperty.value = 'Yes';

    console.log('✓ Subscription properties set:', {
      bundle_quantity: this.currentQuantity,
      subscription: 'Yes'
    });
  }

  /**
   * Clear selling plan and subscription properties
   */
  clearSellingPlan() {
    const productForm = document.querySelector('product-form form');
    if (!productForm) return;

    // Clear selling plan
    const input = productForm.querySelector('[name="selling_plan"]');
    if (input) {
      input.value = '';
    }

    // Clear subscription properties
    const bundleQtyProperty = productForm.querySelector('[name="properties[_subscription_bundle_qty]"]');
    if (bundleQtyProperty) {
      bundleQtyProperty.remove();
    }

    const subscriptionProperty = productForm.querySelector('[name="properties[_subscription]"]');
    if (subscriptionProperty) {
      subscriptionProperty.remove();
    }

    console.log('✓ Selling plan and properties cleared');
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }

    // Use Shopify's money formatter if available
    if (window.Shopify?.formatMoney) {
      const format = window.Shopify.money_format || '${{amount}}';
      return window.Shopify.formatMoney(cents, format);
    }

    // Fallback
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.subscriptionWidgetInstance) {
      window.subscriptionWidgetInstance = new SubscriptionWidget();
    }
  });
} else {
  if (!window.subscriptionWidgetInstance) {
    window.subscriptionWidgetInstance = new SubscriptionWidget();
  }
}

// Reinitialize on Shopify theme editor section reload
document.addEventListener('shopify:section:load', () => {
  window.subscriptionWidgetInstance = new SubscriptionWidget();
});
